/* eslint-disable @typescript-eslint/no-explicit-any */
import { showToast } from '@/components/ui/sonner';
import { AgeRange } from '@/Types/gql/graphql';
import { ApolloError } from '@apollo/client';
import { type ClassValue, clsx } from 'clsx';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { AGE_RANGE_VALUE } from './constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// update the query params
export const updateQueryParam = (paramName: string, paramValue: string | number) => {
  const params = new URLSearchParams(window.location.search);
  const value = paramValue.toString();
  if (value === '') {
    // If value is an empty string, remove the query parameter
    params.delete(paramName);
  } else {
    params.set(paramName, value);
  }

  const newQueryString = params.toString();

  // Check if there are any query parameters left
  const newUrl =
    newQueryString === ''
      ? decodeURIComponent(window.location.pathname)
      : decodeURIComponent(`${window.location.pathname}?${newQueryString}`);

  window.history.pushState(null, '', newUrl);
};

// get the query params
export const getQueryParam = <T extends string | number | undefined>(
  paramName: string,
  defaultValue?: T,
): T => {
  const params = new URLSearchParams(window.location.search);
  const paramValue = params.get(paramName);

  return paramValue !== null ? (paramValue as T) : defaultValue!;
};

// clear all the query params
export const clearAllQueryParams = () => {
  window.history.pushState(null, '', decodeURIComponent(`${window.location.pathname}`));
};

// Function to extract query parameters from the current URL
export const getQueryParams = (id?: string): Record<string, string> => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  const filteredParams: Record<string, string> = {};

  for (const [key, value] of urlParams.entries()) {
    if (id && !key.startsWith(`${id}-`)) {
      continue;
    }
    const modifiedKey = id ? key.replace(`${id}-`, '') : key;
    filteredParams[modifiedKey] = value;
  }

  return filteredParams;
};

// Function to redirect to a new URL with query parameters
export const urlWithParams = (url: string, queryParams: Record<string, string>): string => {
  const urlWithParams = `${url}?${new URLSearchParams(queryParams).toString()}`;
  return urlWithParams;
};

// debounced function
export function useDebouncedFunction<T extends unknown[]>(
  callback: (...args: T) => void,
  delay: number,
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  function debounceFunction(...args: T) {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }

  return debounceFunction;
}

// handle error in mutation and queries
export const handleError = (error: ApolloError) => {
  console.log(`Error: ${capitalizeFirstLetter(error.message)}`);
  if (error.message !== 'Error message not found.') {
    showToast({
      message: capitalizeFirstLetter(error.message),
      variant: 'error',
    });
  }
};

// check image function
export const verifyImage = (file?: File) => {
  if (
    !(
      file?.type == 'image/jpeg' ||
      file?.type == 'image/png' ||
      file?.type == 'image/jpg' ||
      file?.type == 'image/webp'
    )
  ) {
    showToast({
      variant: 'error',
      message: 'This file format is not supported. Please upload only .png or .jpg files',
    });
    return false;
  }
  if (file.size > 5242880) {
    showToast({ variant: 'error', message: 'Please upload a file smaller than 5 MB' });
    return false;
  }
  return true;
};

export const base64ToFile = (dataURI: string, filename: string): File => {
  const byteString: string = atob(dataURI.split(',')[1]);
  const mimeString: string = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab: ArrayBuffer = new ArrayBuffer(byteString.length);
  const ia: Uint8Array = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blob: Blob = new Blob([ab], { type: mimeString });

  const file: File = new File([blob], filename, { type: mimeString });

  return file;
};

// Function to handle formatting of phone number
export const phoneNumberFormat = (num: string) => {
  const newNum = num?.replace(/[-,(,), ]+/g, '');
  let x;
  if (newNum?.length <= 3) {
    x = newNum;
  } else if (newNum?.length > 3 && newNum?.length <= 6) {
    x = '(' + newNum?.slice(0, 3) + ') ' + newNum?.slice(3, 6);
  } else {
    x = '(' + newNum?.slice(0, 3) + ') ' + newNum?.slice(3, 6) + '-' + newNum?.slice(6, 10);
  }
  return x;
};

export function trimText(text: string | undefined, maxLength: number) {
  if (!text) return '';
  if (text.length <= maxLength) {
    return text;
  } else {
    return text.slice(0, maxLength) + '...';
  }
}

// Function to convert enum to filter values
interface FilterValue {
  text: string;
  value: string;
}
export const enumToFilterValues = <T extends Record<string, string>>(
  enumObject: T,
): FilterValue[] => {
  return Object.keys(enumObject).map((key) => ({
    text: key,
    value: enumObject[key],
  }));
};

export const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format;

export const capitalizeFirstLetter = (string: string | undefined) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getFileSize = async (
  url: string,
  callback: (error: Error | null, blob?: Blob) => void,
) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.responseType = 'blob'; // Set response type to blob to handle binary data

  xhr.onload = function () {
    if (xhr.status === 200) {
      const blob = xhr.response;
      callback(null, blob);
    } else {
      callback(new Error(`Failed to fetch file. Status code: ${xhr.status}`));
    }
  };

  xhr.onerror = function () {
    callback(new Error('Network error while fetching file'));
  };

  xhr.send();
};

export function formatFileSize(sizeInBytes: number) {
  const KB = 1024;
  const MB = KB * 1024;

  if (sizeInBytes < KB) {
    return `${sizeInBytes} Bytes`;
  } else if (sizeInBytes < MB) {
    return `${(sizeInBytes / KB).toFixed(2)} KB`;
  } else {
    return `${(sizeInBytes / MB).toFixed(2)} MB`;
  }
}

// get thumbnail of a video
export const generateThumbnail = (videoUrl?: string): Promise<string> => {
  if (!videoUrl) {
    return Promise.reject('Video URL is required');
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    document.body.appendChild(video); // Add the video element to the DOM

    const handleLoadedMetadata = () => {
      video.currentTime = 2; // Set the desired thumbnail time here
    };

    const handleSeeked = () => {
      const context = canvas.getContext('2d');
      if (context) {
        // Ensure the video is ready by waiting a moment before drawing
        setTimeout(() => {
          canvas.width = 320; // Set the canvas width
          canvas.height = 180; // Set the canvas height
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageDataUrl = canvas.toDataURL('image/jpeg');
          document.body.removeChild(video); // Clean up the video element
          resolve(imageDataUrl);
        }, 200); // 200ms delay to ensure video frame is ready
      } else {
        document.body.removeChild(video); // Clean up the video element
        reject('Failed to get canvas context');
      }
    };

    const fetchVideoSegment = async () => {
      try {
        const response = await fetch(videoUrl, {
          headers: {
            Range: 'bytes=0-10000000', // Fetch only the first 10MB of the video
          },
        });
        const buffer = await response.arrayBuffer();
        const blob = new Blob([buffer], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        video.src = url;

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('error', (e) => {
          document.body.removeChild(video); // Clean up the video element
          reject(`Error: ${e.message || 'Failed to load video'}`);
        });
      } catch (error: any) {
        reject(`Error: ${error.message || 'Failed to fetch video segment'}`);
      }
    };

    fetchVideoSegment();
  });
};

export const cmToFeet = (cm: number) => cm / 30.48;
export const feetToCm = (feet: number) => feet * 30.48;
export const kgToLbs = (kg: number) => kg * 2.20462;
export const lbsToKg = (lbs: number) => lbs / 2.20462;
export const cmToInches = (cm: number): number => cm / 2.54;

export function formatHeight(inches: number): string {
  const feet = Math.floor(inches / 12);
  const remainingInches = inches % 12;
  return `${feet}' ${remainingInches}''`;
}

export const ageRangeOptions = Object.entries(AGE_RANGE_VALUE).map(([key, value]) => ({
  text: value,
  value: key as AgeRange,
}));

export const getAgeRangeText = (ageRange: AgeRange): string => AGE_RANGE_VALUE[ageRange];
